import { useCallback, useEffect, useMemo, useState } from "react";
import searchIcon from "../../../assets/img/search-icon.svg";
import { Card } from "../../../components/card.component";
import { ExportButton } from "../../../components/export.component";
import { AppRouteApi } from "../../../config/appRoute";
import {
  ActionsFinanceCaregiverTable,
  useAppDispatch,
} from "../../../core/redux";
import { useAppSelector } from "../../../core/redux/ReduxStore";
import { CaregiverFinanceList } from "../../../features/finance/caregiverFinanceList.component";
import {
  IPaginationSearchFilter,
  useTablePaginationSearchFilter,
} from "../../../hooks";
import { ApiSchemaFilteredCaregiverFinance } from "../../../models/api";
import { StorageAuth } from "../../../core/localStorage";
import { ApiCaregiver, ApiPatient } from "../../../core/apis";
import { SelectPicker } from "rsuite";

export const PageFinanceCaregiver = () => {
  const dispatch = useAppDispatch();
  const { pageNumber, pageSize, searchKey, nextPageToken, shouldRefetch } =
    useAppSelector((state) => state.financeCaregiverTable);

  const selectedCompany = StorageAuth.CompanyId || '';
  // State for filters
  const [selectedPatient, setSelectedPatient] = useState('');
  const [selectedCaregiver, setSelectedCaregiver] = useState('');
  const { data: patients } = ApiPatient.GetAllFromCompany.Hook(selectedCompany);
  const { data: caregivers } = ApiCaregiver.GetAllFromCompany.Hook(selectedCompany);

  const apiParams = useMemo<IPaginationSearchFilter['customParams']>(() => {
    return {
      NextPageToken: nextPageToken,
      PageNumber: pageNumber,
      PageSize: pageSize,
      SearchKey: searchKey,
      PatientSKey: selectedPatient || undefined,
      CaregiverSKey: selectedCaregiver || undefined,
    };
  }, [nextPageToken, pageNumber, pageSize, searchKey, selectedPatient, selectedCaregiver]);

  const {
    apiData: financeData,
    isFetching: isFetchingFinanceData,
    refetch,
  } = useTablePaginationSearchFilter<
    ApiSchemaFilteredCaregiverFinance,
    typeof ActionsFinanceCaregiverTable
  >(ActionsFinanceCaregiverTable, {
    customParams: apiParams,
    apiRoute: AppRouteApi.Finance.CaregiverV2(),
  });

  const searchCaregiverList = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>): void => {
      dispatch(ActionsFinanceCaregiverTable.goToPage(1));
      dispatch(ActionsFinanceCaregiverTable.setSearchKey(ev.target.value));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    shouldRefetch && refetch();
  }, [shouldRefetch, refetch]);

  useEffect(() => {
    return () => {
      dispatch(ActionsFinanceCaregiverTable.resetData());
    };
  }, [dispatch]);

  const patientsSelectItems = patients?.map((patient) => ({
    label: `${patient.firstName} ${patient.lastName}`,
    value: patient.sKey,
  })) || [];

  const caregiverSelectItems = useMemo(() => {
    return caregivers?.map((caregiver) => ({
      label: `${caregiver.firstName} ${caregiver.lastName}`,
      value: caregiver.sKey,
    })) || [];
  }, [caregivers]);

  return (
    <Card
      header={
        <div className="row align-items-center  justify-content-between">
          <div className="col-md-8 col-12 content-box-title">
            <div>
              <p className="text-capitalize my-1 text-green-200">
                Caregivers` Finance
              </p>
            </div>
            <div className="collapse-icon"></div>
          </div>
          <div className="col-md-4 col-12 mt-1">
            <form className="search-component">
              <div className="input-group me-3">
                <span
                  className="input-group-text pe-2"
                  id="search-component-word"
                >
                  <img src={searchIcon} alt="search-icon" />
                </span>
                <input
                  type="text"
                  className="form-control ps-0"
                  placeholder="Search..."
                  aria-label="Search"
                  aria-describedby="search-component-word"
                  onChange={searchCaregiverList}
                  value={searchKey}
                  onKeyPress={(ev) => {
                    ev.key === "Enter" && ev.preventDefault();
                  }}
                />
                {searchKey.length > 0 && (
                  <span
                    className="input-group-text text-green-200"
                    id="search-component-word"
                  >
                    <i
                      className="fas fa-times click"
                      onClick={() => {
                        dispatch(ActionsFinanceCaregiverTable.setSearchKey(""));
                      }}
                    />
                  </span>
                )}
              </div>
              <ExportButton
                params={{ pageNumber, pageSize, searchKey }}
                component="Caregiver"
              />
            </form>
          </div>
        </div>
      }
    >
      <div className='col-12 mb-3'>
        <div className='d-flex flex-wrap gap-3 p-3 border rounded bg-light'>
          <div className='d-flex align-items-center gap-2'>
            <span className='fw-bold'>Filters:</span>
          </div>
          <div className='d-flex align-items-center gap-2'>
            <span>Patient</span>
            <div style={{ minWidth: '200px' }}>
              <SelectPicker
                data={patientsSelectItems}
                value={selectedPatient}
                onChange={(value) => {
                  setSelectedPatient(value as string);
                  setSelectedCaregiver('');
                }}
                searchable
                cleanable
                placeholder='Select Patient'
                block
              />
            </div>
          </div>
          <div className='d-flex align-items-center gap-2'>
            <span>Caregiver</span>
            <div style={{ minWidth: '200px' }}>
              <SelectPicker
                data={caregiverSelectItems}
                value={selectedCaregiver}
                onChange={(value) => setSelectedCaregiver(value as string)}
                searchable
                cleanable
                placeholder={'Select Caregiver'}
                block
              />
            </div>
          </div>
        </div>
      </div>
      
      <CaregiverFinanceList
        data={financeData?.list}
        totalCount={financeData?.totalCount || 0}
        isValidating={isFetchingFinanceData}
      />
    </Card>
  );
};
